import banking from "@/data/banking.json";
import construction from "@/data/construction.json";
import finance from "@/data/finance.json";
import healthcare from "@/data/healthcare.json";
import manufacturing from "@/data/manufacturing.json";
import realEstate from "@/data/real-estate.json";
import retail from "@/data/retail_and_e-commerce.json";
import technologySaas from "@/data/technology-saas.json";

import type { WorkflowId } from "./types";

export type SectorQuestionType = "count" | "percentage" | "currency";

export type SectorQuestionCategory =
  | "Labor Waste"
  | "Yield Penalty"
  | "Untapped Value";

export type SectorQuestion = {
  category: SectorQuestionCategory;
  v: number;
  type: SectorQuestionType;
  question: string;
};

export type FrictionPoint = {
  title: string;
  questions: SectorQuestion[];
};

export type SectorCatalogEntry = {
  sector: string;
  friction_points: FrictionPoint[];
};

const FRICTION_TITLE_TO_WORKFLOW_ID: Record<string, WorkflowId> = {
  "Custom Sales & Quoting": "custom-sales",
  "Field Service & Dispatch": "field-service",
  "High-Volume Staffing": "high-volume",
  "Professional Services & Reporting": "professional-services",
};

const CATALOG: readonly SectorCatalogEntry[] = [
  banking,
  finance,
  construction,
  healthcare,
  manufacturing,
  realEstate,
  retail,
  technologySaas,
] as SectorCatalogEntry[];

const CATALOG_BY_SECTOR: Record<string, SectorCatalogEntry> =
  Object.fromEntries(CATALOG.map((entry) => [entry.sector, entry]));

/**
 * Display order for the intro sector dropdown.
 */
export const CATALOG_SECTORS = [
  "Banking",
  "Financial Services",
  "Construction",
  "Healthcare",
  "Manufacturing",
  "Real Estate",
  "Retail & E-Commerce",
  "Technology / SaaS",
] as const;

export type CatalogSector = (typeof CATALOG_SECTORS)[number];

export function listSectorCatalog(): readonly SectorCatalogEntry[] {
  return CATALOG;
}

export function isCatalogSector(value: string): value is CatalogSector {
  return (CATALOG_SECTORS as readonly string[]).includes(value);
}

export function getSectorCatalog(
  sector: string | null | undefined,
): SectorCatalogEntry | null {
  if (!sector) return null;
  return CATALOG_BY_SECTOR[sector] ?? null;
}

export function listFrictionPoints(
  sector: string | null | undefined,
): readonly FrictionPoint[] {
  return getSectorCatalog(sector)?.friction_points ?? [];
}

export function frictionTitleToWorkflowId(title: string): WorkflowId | null {
  return FRICTION_TITLE_TO_WORKFLOW_ID[title] ?? null;
}

export function workflowIdToFrictionTitle(workflowId: WorkflowId): string {
  const entry = Object.entries(FRICTION_TITLE_TO_WORKFLOW_ID).find(
    ([, id]) => id === workflowId,
  );
  return entry?.[0] ?? workflowId;
}

export function getFrictionPoint(
  sector: string | null | undefined,
  workflowId: WorkflowId,
): FrictionPoint | null {
  const title = workflowIdToFrictionTitle(workflowId);
  return (
    listFrictionPoints(sector).find((point) => point.title === title) ?? null
  );
}
